import type { CustomFieldDTO } from '@tempo-io/item-store-dto';
import { customFieldSchema } from '@tempo-io/item-store-dto';
import type { HeaderSelector } from '@tempo-io/swimlane-transforms';
import { z } from 'zod';
import { type SwimlaneLayoutDTO, swimlaneLayoutDTOSchema } from './layout-dto';
import {
  type PublishedEstimation,
  type PublishedEstimationScheme,
  type PublishedItemLevel,
  type PublishedItemStatus,
  type PublishedItemType,
  type PublishedTeam,
  publishedEstimationSchema,
  publishedEstimationSchemeSchema,
  publishedItemLevelSchema,
  publishedItemStatusSchema,
  publishedItemTypeSchema,
  publishedTeamSchema,
} from './published-lookup-dto';

export type {
  PublishedEstimation,
  PublishedEstimationScheme,
  PublishedItemLevel,
  PublishedItemStatus,
  PublishedItemType,
  PublishedTeam,
};

/**
 * Display value attached to headers/labels for rich chip rendering
 * (e.g. customer name + logo for MULTI_CUSTOMER headers).
 * Mirrors the frontend `HeaderDisplayValue` shape so the published
 * payload can carry server-resolved identity for unauthenticated viewers.
 */
export interface CustomerHeaderDisplayValue {
  type: 'customer';
  id: string;
  name: string;
  logoUrl?: string | null;
}

export type HeaderDisplayValue = CustomerHeaderDisplayValue;

const customerHeaderDisplayValueSchema = z.object({
  type: z.literal('customer'),
  id: z.string(),
  name: z.string(),
  logoUrl: z.string().nullable().optional(),
});

export const headerDisplayValueSchema = z.discriminatedUnion('type', [
  customerHeaderDisplayValueSchema,
]);

/**
 * Header type for column and row headers in published swimlanes.
 * Matches the frontend's IHeader interface from ui/swimlane/types.
 */
export interface IHeader {
  id: string;
  label: string;
  selector: HeaderSelector;
  level: 1 | 2 | 3;
  headerIndex: number;
  isDateHeader: boolean;
  headerColor?: string;
  isGroupHeader?: boolean;
  startDate?: string;
  endDate?: string;
  displayValues?: HeaderDisplayValue[];
}

/**
 * Label with field information for item cards
 */
export interface LabelWithField {
  value: string;
  fieldName: string;
  displayValues?: HeaderDisplayValue[];
}

/**
 * Label with field information schema
 */
const labelWithFieldSchema = z.object({
  value: z.string(),
  fieldName: z.string(),
  displayValues: z.array(headerDisplayValueSchema).optional(),
});

/**
 * Sub-item reference in published swimlane items.
 * Optionally carries one further level of nested children when the swimlane
 * layout requests `childItemLevels >= 2`.
 */
export interface SubItemReference {
  id: string;
  title: string;
  itemTypeId: string;
  itemTypeName: string;
  subItems?: SubItemReference[];
}

/**
 * Enriched item type for published swimlanes
 * Contains all item data plus enriched team and status information
 */
export interface IdentifiableTItem {
  id: number;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  progress: number | null;
  isDeletable: boolean;
  attachments: number;
  borderColor: string;
  comments: number;
  keyDates: number;
  linkedItems: number;
  labels: LabelWithField[];
  subItems: SubItemReference[];

  // Item type information
  type: string; // Item type name (e.g., "Epic", "Story", "Task")
  itemTypeId: number;
  itemTypeIcon?: string;
  itemTypeColor?: string;

  // Optional parent reference
  parentId?: string;
  parentTitle?: string;
  parentItemTypeIcon?: string;
  parentItemTypeColor?: string;

  // Team information
  teamId?: number;
  teamName?: string;
  teamColor?: string;

  // Status information
  itemStatusId?: number;
  statusName?: string;
  statusType?: string;
  statusColor?: string;

  // Estimation information
  estimationLabel?: string;
}

/**
 * Item dependency for published swimlanes
 */
export interface ItemDependency {
  dependencyType: 'PREDECESSOR' | 'SUCCESSOR';
  dependencyItemId: number;
}

/**
 * Ancestor reference for breadcrumb navigation in published item panels
 */
export interface ItemAncestorRef {
  id: number;
  name: string;
  depth: number;
}

export interface ItemSchedule {
  expectedStartDateTime: string | null;
  expectedFinishDateTime: string | null;
  latestFinishDateTime: string | null;
}

export interface ResolvedUser {
  name: string;
  pictureUrl: string | null;
}

export interface ResolvedItem {
  id: number;
  name: string;
  itemTypeId: number;
}

export interface ResolvedCustomer {
  id: number;
  name: string;
}

/**
 * Detailed item information from ItemClient
 * Contains all fields from work items for panel details
 */
export interface ItemDetails {
  id: number;
  name: string;
  description: string | null;
  itemTypeId: number;
  itemStatusId: number | undefined;
  parentId: number | null;
  teamId: number | null;
  targetStartDateTime: string | null;
  targetFinishDateTime: string | null;
  plannedStartDateTime?: string | null;
  plannedFinishDateTime?: string | null;
  customFields: Record<string, unknown>;
  timeSpentSeconds: number | null;
  estimationId: number | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  doneDateTime: string | null;
  externalKey: string | null;
  externalLink: string | null;
  planId: number | null;
  dependencies: ItemDependency[];
  ancestors?: readonly ItemAncestorRef[];
  schedule?: ItemSchedule | null;
  progress?: number | null;
}

/**
 * Legend item representing a color-to-category mapping for the swimlane legend.
 */
export interface LegendItem {
  color: string;
  label: string;
  isHidden?: boolean;
}

/**
 * Publishing preferences for controlling what elements are displayed in published views.
 * Derived from LayoutOptionsDTO with semantic naming for frontend consumption.
 */
export interface PublishingPreferences {
  showTitle: boolean;
  showVisionStatement: boolean;
  showPublishDate: boolean;
  showLegend: boolean;
  showTempoBranding: boolean;
}

/**
 * Metadata for published swimlane views
 */
export interface PublishedMetadata {
  title: string;
  viewId: number;
  createdAt: string;
  publishedAt: string;
  visionStatement?: string;
  preferences?: PublishingPreferences;
}

/**
 * Server-resolved lookup of customer entity references keyed by customer id.
 * Populated alongside MULTI_CUSTOMER header/label rendering so anonymous
 * viewers see customer name + identity without needing FIM access.
 */
export interface CustomerLookupEntry {
  id: number;
  name: string;
  logoUrl?: string | null;
}

/**
 * Response type for published swimlane endpoint
 * Contains all data needed to render a published swimlane view
 */
export interface PublishedSwimlaneResponse {
  items: Record<string, IdentifiableTItem>;
  columnHeaders: IHeader[];
  rowHeaders: IHeader[];
  cells: Record<string, Record<string, string[]>>;
  metadata: PublishedMetadata;
  layout?: SwimlaneLayoutDTO;
  itemTypes: PublishedItemType[];
  itemLevels: PublishedItemLevel[];
  itemStatuses: PublishedItemStatus[];
  teams: PublishedTeam[];
  estimations: PublishedEstimation[];
  estimationSchemes: PublishedEstimationScheme[];
  itemDetails: Record<string, ItemDetails>;
  customFieldDefinitions: CustomFieldDTO[];
  columnAxisLabel: string;
  rowAxisLabel: string;
  legendItems?: LegendItem[];
  hiddenFieldIds?: readonly string[];
  columnHiddenHeaderIds?: string[];
  rowHiddenHeaderIds?: string[];
  customerLookup?: Record<string, CustomerLookupEntry>;
  resolvedUsers?: Record<string, ResolvedUser>;
  resolvedItems?: Record<string, ResolvedItem>;
  resolvedCustomers?: Record<string, ResolvedCustomer>;
}

// =============================================================================
// Zod Schemas for runtime validation
// =============================================================================

/**
 * HeaderSelector schemas - discriminated union matching @tempo-io/swimlane-transforms
 */
const customPropertySelectorSchema = z.object({
  type: z.literal('CustomPropertySelectorByID'),
  customPropertyID: z.string(),
});

const parentItemSelectorSchema = z.object({
  type: z.literal('ParentItemSelector'),
});

const dateHeaderSelectorSchema = z.object({
  type: z.literal('DateSelector'),
  header: z.string(),
});

const createdByHeaderSelectorSchema = z.object({
  type: z.literal('createdBy'),
});

export const headerSelectorSchema = z.discriminatedUnion('type', [
  customPropertySelectorSchema,
  parentItemSelectorSchema,
  dateHeaderSelectorSchema,
  createdByHeaderSelectorSchema,
]);

/**
 * SubItemReference schema (recursive: optional nested subItems for grand-children).
 */
export const subItemReferenceSchema: z.ZodType<SubItemReference> = z.lazy(() =>
  z.object({
    id: z.string(),
    title: z.string(),
    itemTypeId: z.string(),
    itemTypeName: z.string(),
    subItems: z.array(subItemReferenceSchema).optional(),
  }),
);

/**
 * IHeader schema - matches frontend IHeader interface
 */
export const iHeaderSchema = z.object({
  id: z.string(),
  label: z.string(),
  selector: headerSelectorSchema,
  level: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  headerIndex: z.number().int(),
  isDateHeader: z.boolean(),
  headerColor: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  displayValues: z.array(headerDisplayValueSchema).optional(),
});

/**
 * IdentifiableTItem schema - enriched item with team/status information
 */
export const identifiableTItemSchema = z.object({
  id: z.number().int(),
  title: z.string(),
  description: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  progress: z.number().nullable(),
  isDeletable: z.boolean(),
  attachments: z.number().int(),
  borderColor: z.string(),
  comments: z.number().int(),
  keyDates: z.number().int(),
  linkedItems: z.number().int(),
  labels: z.array(labelWithFieldSchema),
  subItems: z.array(subItemReferenceSchema),
  type: z.string(),
  itemTypeId: z.number().int(),
  itemTypeIcon: z.string().optional(),
  itemTypeColor: z.string().optional(),
  parentId: z.string().optional(),
  parentTitle: z.string().optional(),
  parentItemTypeIcon: z.string().optional(),
  parentItemTypeColor: z.string().optional(),
  teamId: z.number().int().optional(),
  teamName: z.string().optional(),
  teamColor: z.string().optional(),
  itemStatusId: z.number().int().optional(),
  statusName: z.string().optional(),
  statusType: z.string().optional(),
  statusColor: z.string().optional(),
});

/**
 * ItemDependency schema
 */
const itemDependencySchema = z.object({
  dependencyType: z.enum(['PREDECESSOR', 'SUCCESSOR']),
  dependencyItemId: z.number().int(),
});

/**
 * ItemAncestorRef schema
 */
export const itemAncestorRefSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  depth: z.number().int(),
});

const itemScheduleSchema = z.object({
  expectedStartDateTime: z.string().nullable(),
  expectedFinishDateTime: z.string().nullable(),
  latestFinishDateTime: z.string().nullable(),
});

export const resolvedUserSchema = z.object({
  name: z.string(),
  pictureUrl: z.string().nullable(),
});

export const resolvedItemSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  itemTypeId: z.number().int(),
});

export const resolvedCustomerSchema = z.object({
  id: z.number().int(),
  name: z.string(),
});

/**
 * ItemDetails schema - detailed item information from ItemClient
 */
export const itemDetailsSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  description: z.string().nullable(),
  itemTypeId: z.number().int(),
  itemStatusId: z.union([z.number().int(), z.undefined()]),
  parentId: z.number().int().nullable(),
  teamId: z.number().int().nullable(),
  targetStartDateTime: z.string().nullable(),
  targetFinishDateTime: z.string().nullable(),
  plannedStartDateTime: z.string().nullable().optional(),
  plannedFinishDateTime: z.string().nullable().optional(),
  customFields: z.record(z.string(), z.unknown()),
  timeSpentSeconds: z.number().nullable(),
  estimationId: z.number().int().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: z.string(),
  updatedBy: z.string(),
  doneDateTime: z.string().nullable(),
  externalKey: z.string().nullable(),
  externalLink: z.string().nullable(),
  planId: z.number().int().nullable(),
  dependencies: z.array(itemDependencySchema),
  ancestors: z.array(itemAncestorRefSchema).readonly().optional(),
  schedule: itemScheduleSchema.nullable().optional(),
  progress: z.number().int().nullable().optional(),
});

/**
 * LegendItem schema
 */
export const legendItemSchema = z.object({
  color: z.string(),
  label: z.string(),
  isHidden: z.boolean().optional(),
});

/**
 * PublishingPreferences schema
 */
export const publishingPreferencesSchema = z.object({
  showTitle: z.boolean(),
  showVisionStatement: z.boolean(),
  showPublishDate: z.boolean(),
  showLegend: z.boolean(),
  showTempoBranding: z.boolean(),
});

/**
 * PublishedMetadata schema
 */
export const publishedMetadataSchema = z.object({
  title: z.string(),
  viewId: z.number().int(),
  createdAt: z.string(),
  publishedAt: z.string(),
  visionStatement: z.string().optional(),
  preferences: publishingPreferencesSchema.optional(),
});

/**
 * CustomerLookupEntry schema - server-resolved customer entity reference
 */
export const customerLookupEntrySchema = z.object({
  id: z.number().int(),
  name: z.string(),
  logoUrl: z.string().nullable().optional(),
});

/**
 * PublishedSwimlaneResponse schema - main response type for published swimlane endpoint
 */
export const publishedSwimlaneResponseSchema = z.object({
  items: z.record(z.string(), identifiableTItemSchema),
  columnHeaders: z.array(iHeaderSchema),
  rowHeaders: z.array(iHeaderSchema),
  cells: z.record(z.string(), z.record(z.string(), z.array(z.string()))),
  metadata: publishedMetadataSchema,
  layout: swimlaneLayoutDTOSchema.optional(),
  itemTypes: z.array(publishedItemTypeSchema).default([]),
  itemLevels: z.array(publishedItemLevelSchema).default([]),
  itemStatuses: z.array(publishedItemStatusSchema).default([]),
  teams: z.array(publishedTeamSchema).default([]),
  estimations: z.array(publishedEstimationSchema).default([]),
  estimationSchemes: z.array(publishedEstimationSchemeSchema).default([]),
  itemDetails: z.record(z.string(), itemDetailsSchema),
  customFieldDefinitions: z.array(customFieldSchema).optional().default([]),
  columnAxisLabel: z.string(),
  rowAxisLabel: z.string(),
  legendItems: z.array(legendItemSchema).optional(),
  hiddenFieldIds: z.array(z.string()).optional(),
  columnHiddenHeaderIds: z.array(z.string()).optional(),
  rowHiddenHeaderIds: z.array(z.string()).optional(),
  customerLookup: z.record(z.string(), customerLookupEntrySchema).optional(),
  resolvedUsers: z.record(z.string(), resolvedUserSchema).optional(),
  resolvedItems: z.record(z.string(), resolvedItemSchema).optional(),
  resolvedCustomers: z.record(z.string(), resolvedCustomerSchema).optional(),
});

// Type alignment check - verify schema output is assignable to interface
// Note: We check one direction only because Zod produces mutable types while
// the interface uses `readonly` for HeaderSelector (from swimlane-transforms).
// At runtime, the values are identical - readonly is a compile-time constraint only.
type SchemaType = z.infer<typeof publishedSwimlaneResponseSchema>;
type _AssignableToInterface = SchemaType extends PublishedSwimlaneResponse ? true : never;
const _typeCheck: _AssignableToInterface = true;
void _typeCheck;
